/* TypeScript DTOs for Training API */

import { TrainingStatus, QuestionType } from '../../../../../data/complianceMockData';

export enum TrainingAssignmentStatus {
  Assigned = 0,
  InProgress = 1,
  Completed = 2,
  Failed = 3,
}

/** Training Category DTOs */
export interface TrainingCategoryDto {
  id: number;
  franchiseIds: number[];
  name: string;
  description: string | null;
  displayOrder: number;
  isActive: boolean;
}

export interface CreateTrainingCategoryDto {
  franchiseIds: number[];
  name: string;
  description?: string | null;
  displayOrder: number;
  isActive: boolean;
}

export interface UpdateTrainingCategoryDto {
  franchiseIds: number[];
  name: string;
  description?: string | null;
  displayOrder: number;
  isActive: boolean;
}

/** Training Module Content DTOs */
export interface TrainingModuleContentDto {
  id: number;
  moduleId: number;
  title: string;
  contentType: 'text' | 'video' | 'document' | 'link';
  contentText: string | null;
  contentUrl: string | null;
  contentSasUrl: string | null;
  displayOrder: number;
  estimatedMinutes: number;
}

export interface TrainingModuleSectionDetailDto {
  id: number;
  moduleId: number;
  title: string;
  contentType: 'text' | 'video' | 'document' | 'link';
  contentText: string | null;
  contentUrl: string | null;
  contentSasUrl: string | null;
  displayOrder: number;
  estimatedMinutes: number;
}

export interface CreateSectionRequestDto {
  title: string;
  contentType: 'text' | 'video' | 'document' | 'link';
  contentText?: string | null;
  contentUrl?: string | null;
  displayOrder: number;
  estimatedMinutes: number;
}

export interface UpdateSectionRequestDto {
  title: string;
  contentText?: string | null;
  contentUrl?: string | null;
  displayOrder: number;
  estimatedMinutes: number;
}

export interface CreateTrainingContentDto {
  title: string;
  contentType: 'text' | 'video' | 'document' | 'link';
  contentText?: string | null;
  contentUrl?: string | null;
  displayOrder: number;
  estimatedMinutes: number;
}

/** Training Quiz Answer DTOs */
export interface TrainingQuizAnswerDto {
  id: number;
  questionId: number;
  answerText: string;
  isCorrect: boolean;
  displayOrder: number;
}

export interface CreateTrainingQuizAnswerDto {
  answerText: string;
  isCorrect: boolean;
  displayOrder: number;
}

/** Training Quiz Question DTOs */
export interface TrainingQuizQuestionDto {
  id: number;
  quizId: number;
  questionText: string;
  questionType: QuestionType;
  points: number;
  displayOrder: number;
  answers: TrainingQuizAnswerDto[];
}

export interface CreateTrainingQuizQuestionDto {
  questionText: string;
  questionType: QuestionType;
  points: number;
  displayOrder: number;
  answers: CreateTrainingQuizAnswerDto[];
}

/** Training Quiz DTOs */
export interface TrainingModuleQuizDto {
  id: number;
  moduleId: number;
  title: string;
  description: string | null;
  passingScore: number;
  questions: TrainingQuizQuestionDto[];
}

export interface CreateTrainingQuizDto {
  title: string;
  description?: string | null;
  passingScore: number;
  questions: CreateTrainingQuizQuestionDto[];
}

export interface CreateQuizStandaloneRequestDto {
  title: string;
  description?: string | null;
  passingScore: number;
  questions: {
    questionText: string;
    questionType: number;
    points: number;
    displayOrder: number;
    answers: {
      answerText: string;
      isCorrect: boolean;
      displayOrder: number;
    }[];
  }[];
}

export interface UpdateQuizStandaloneRequestDto {
  title: string;
  description?: string | null;
  passingScore: number;
  questions: {
    id?: number;
    questionText: string;
    questionType: number;
    points: number;
    displayOrder: number;
    answers: {
      id?: number;
      answerText: string;
      isCorrect: boolean;
      displayOrder: number;
    }[];
  }[];
}

/** Training Module DTOs */
export interface TrainingModuleDto {
  id: number;
  franchiseIds: number[];
  categoryId: number;
  categoryName: string;
  name: string;
  description: string | null;
  version: number;
  status: number; // TrainingStatus enum value
  estimatedDurationMinutes: number;
  validityPeriodDays: number | null;
  createdDate: string;
  publishedDate: string | null;
}

export interface TrainingModuleDetailDto {
  id: number;
  franchiseIds: number[];
  categoryId: number;
  categoryName: string;
  name: string;
  description: string | null;
  version: number;
  status: number; // TrainingStatus enum value
  estimatedDurationMinutes: number;
  validityPeriodDays: number | null;
  createdDate: string;
  publishedDate: string | null;
  passingScore: number | null;
  content: TrainingModuleContentDto[];
  quizzes: TrainingModuleQuizDto[];
}

export interface CreateTrainingModuleDto {
  categoryId: number;
  franchiseIds: number[];
  name: string;
  description?: string | null;
  estimatedDurationMinutes: number;
  validityPeriodDays?: number | null;
  passingScore?: number | null; 
  content?: CreateTrainingContentDto[];
  quizzes?: CreateTrainingQuizDto[];
}

// ...existing types...

export interface UpdateTrainingModuleDto {
  categoryId: number;
  franchiseIds: number[];
  name: string;
  description: string | null;
  status?: number;
  estimatedDurationMinutes: number;
  validityPeriodDays: number | null;
  passingScore: number | null; 
  content: UpdateTrainingContentDto[];
  quizzes: UpdateTrainingQuizDto[];
}

export interface UpdateTrainingContentDto {
  id?: number; 
  title: string;
  contentType: string;
  contentText: string | null;
  contentUrl: string | null;
  displayOrder: number;
  estimatedMinutes: number;
}

export interface UpdateTrainingQuizDto {
  id?: number;
  title: string;
  description: string | null;
  passingScore: number;
  questions: UpdateTrainingQuizQuestionDto[];
}

export interface UpdateTrainingQuizQuestionDto {
  id?: number;
  questionText: string;
  questionType: number;
  points: number;
  displayOrder: number;
  answers: UpdateTrainingQuizAnswerDto[];
}

export interface UpdateTrainingQuizAnswerDto {
  id?: number;
  answerText: string;
  isCorrect: boolean;
  displayOrder: number;
}

/** Training Assignment/Completion DTOs */
export interface TrainingAssignmentDto {

  id: number;
  userId: number;
  moduleId: number;
  moduleName: string;
  userName: string;
  userEmail: string;
  categoryName: string;
  assignedDate: string;
  startedDate: string | null;
  completedDate: string | null;
  dueDate: string | null;
  expiryDate: string | null;
  currentStep: number;
  totalSteps: number;
  status: string; // e.g., "Pending", "InProgress", "Completed", "Expired"
  score: number | null;
  quizPassed: boolean | null;
}

export interface PagedData<T> {
  totalCount: number;
  skip: number;
  take: number;
  data: T[];
}

export interface TrainingAssignmentListFilters {
  storeNumber?: string;
  franchiseId?: number;
  search?: string;
  statusId?: number;
  skip?: number;
  take?: number;
}

export interface StartTrainingDto {
  assignmentId: number;
  startedAt: string;
}

export interface SubmitQuizDto {
  assignmentId: number;
  answers: QuizAnswerSubmissionDto[];
}

export interface QuizAnswerSubmissionDto {
  questionId: number;
  selectedAnswerIds: number[];
}

export interface QuizResultDto {
  assignmentId: number;
  score: number;
  totalPoints: number;
  passed: boolean;
  passingScore: number;
  completedDate: string;
}

/** Training Assignments DTOs */
export interface PersonaOptionDto {
  id: number;
  name: string;
  description: string | null;
}

export interface UserOptionDto {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  personaId: number | null;
  personaName: string | null;
}

/** Store Option DTO */
export interface StoreOptionDto {
  storeNumber: string;
  storeName: string;
}

/** Franchise Option DTO (for accessible-franchises endpoint) */
export interface TrainingFranchiseOptionDto {
  id: number;
  name: string;
}

/** Bulk Assignment DTOs */
export interface BulkAssignTrainingRequestDto {
  moduleId: number;
  storeNumber?: string;
  assignmentType: 'user' | 'role' | 'store';
  userIds?: number[];
  roleIds?: number[];
  storeNumbers?: string[];
  dueDate?: string | null;
  sendNotification: boolean;
  notificationMessage?: string | null;
}

export interface BulkAssignTrainingResponseDto {
  assignedCount: number;
  failedCount: number;
  assignmentIds: number[];
  errors?: string[];
}

/** Training Certificate DTOs */
export interface TrainingCertificateDto {
  id: number;
  userId: number;
  userName: string;
  moduleId: number;
  moduleName: string;
  completionId: number;
  issueDate: string;
  validUntil: string | null;
  certificateNumber: string;
  isRevoked: boolean;
  revokedDate: string | null;
  revokedReason: string | null;
}

/** Training Reports DTOs */
export interface TrainingReportFilterDto {
  startDate: string; // ISO date string
  endDate: string; // ISO date string
  storeIds?: number[];
  assignmentStatuses?: number[]; // 0=Assigned, 1=InProgress, 2=Completed, 3=Failed
  moduleIds?: number[];
  userIds?: number[];
  franchiseId?: number;
}

/** Training Summary Report DTOs - matches backend API structure */
export interface TrainingSummaryReportDto {
  reportGeneratedDate: string;
  startDate: string;
  endDate: string;
  totalAssignments: number;
  completedCount: number;
  averageScore: number;
  passedCount: number;
  failedCount: number;
  moduleSummaries: TrainingModuleSummaryDto[];
  assignmentDetails: TrainingAssignmentDetailDto[];
}

export interface TrainingModuleSummaryDto {
  moduleName: string;
  categoryName: string;
  totalAssignments: number;
  completedCount: number;
  completionRate: number;
  averageScore: number;
  passedCount: number;
  failedCount: number;
}

export interface TrainingAssignmentDetailDto {
  moduleName: string;
  categoryName: string;
  assignedDate: string;
  dueDate: string | null;
  completedDate: string | null;
  status: string;
  score: number | null;
  attemptCount: number;
}

/** Training Completion Report DTOs - matches backend API structure */
export interface TrainingCompletionReportDto {
  reportGeneratedDate: string;
  startDate: string;
  endDate: string;
  totalCompletions: number;
  averageScore: number;
  userCompletions: TrainingUserCompletionDto[];
}

export interface TrainingUserCompletionDto {
  userName: string;
  moduleName: string;
  categoryName: string;
  assignedDate: string;
  dueDate: string | null;
  completedDate: string;
  score: number;
  passed: boolean;
  attemptsCount: number;
  hasCertificate: boolean;
}

export interface TrainingReportExportRequestDto {
  reportType: 'TrainingSummary' | 'TrainingCompletion';
  startDate: string;
  endDate: string;
  storeIds?: number[];
  assignmentStatuses?: number[];
  moduleIds?: number[];
  userIds?: number[];
  exportFormat?: 'PDF' | 'Excel' | 'CSV';
  franchiseId?: number | null;
}

export interface TrainingReportExportResultDto {
  fileData: Blob;
  contentType: string;
  fileName: string;
}

/** Training Dashboard DTOs */
export interface TrainingDashboardFilter {
  franchiseId: number;
  storeNumbers?: string[] | null;
  regionIds?: string[] | null;
}

export interface TrainingDashboardMetricsDto {
  // Core Metrics
  totalAssignments: number;
  completedAssignments: number;
  inProgressAssignments: number;
  failedAssignments: number;
  overdueAssignments: number;
  expiredAssignments: number;
  
  // Completion Rates
  overallCompletionRate: number;
  averageScore: number | null;
  passRate: number;
  
  // Certificates
  certificatesIssued: number;
  certificatesExpiringSoon: number; // Within 30 days
  certificatesExpired: number;
  
  // Trends (vs previous period)
  completionRateTrend: number | null; // % change
  averageScoreTrend: number | null; // % change
}

export interface TrainingCompletionTrendDto {
  month: string;
  year: number;
  assignedCount: number;
  completedCount: number;
  completionRate: number;
  averageScore: number | null;
}

export interface StoreTrainingPerformanceDto {
  storeId: number;
  storeNumber: string;
  storeName: string;
  totalAssignments: number;
  completedAssignments: number;
  completionRate: number;
  averageScore: number | null;
  overdueCount: number;
  failedCount: number;
}
